import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { StringFormatter } from '@repo/toolkit/string'
import InnerBox from '@repo/ui/inner-box'
import TextLink from '@repo/ui/text-link'
import Typography from '@repo/ui/typography'

import { API, constructApiUrl } from '../../../../api/apis.config'
import { getCenter } from '../../../../api/centers/get-center'
import { Paths } from '../../../../lib/constants/path'
import { useDataProps } from '../../../../lib/hooks/use-data-props'
import { genderOneLetterMapping } from '../../../../lib/types/enums/common/gender.enum'
import { schoolLevelEnumMapping } from '../../../../lib/types/enums/common/school-level.enum'
import { CenterBasic } from '../../../../lib/types/models/center/center'
import { EnrollmentProperties } from '../../../../lib/types/models/enrollment/enrollment.properties'
import { EnrollmentCenter } from '../../../../lib/types/models/enrollment-center/enrollment-center'
import { DataProps } from '../../../../lib/types/utils'
import { generateColumns } from '../../../../utils/functions/generate-columns'
import AccomodationTooltip from '../../../elements/accomodation-tooltip'
import DynamicDataTable from '../../../elements/dynamic-table'
import OlCenterAffectedStaffTable from '../../ol-common/ol-center-affected-staff-table'
import OlCenterEnrollmentTableFriendsTooltip from '../../ol-common/ol-center-placement-enrollment-table/ol-center-enrollment-table-friends-tooltip'
import OlCenterEnrollmentTableHostFamily from '../../ol-common/ol-center-placement-enrollment-table/ol-center-enrollment-table-host-family-tooltip'

export const columns = generateColumns<EnrollmentCenter>({
  order: [
    'enrollment.childLastName',
    'enrollment.childFirstName',
    'enrollment.childBirthDate',
    'enrollment.childGender',
    'enrollment.childSchoolLevel',
    'enrollment.mainProposition.familyArrangement',
    'enrollment.declaredFriends',
    'hostFamily.parent1LastName'
  ],
  headers: {
    'enrollment.childLastName': 'Nom du jeune',
    'enrollment.childFirstName': ' Prénom du jeune',
    'enrollment.childBirthDate': 'Âge',
    'enrollment.childGender': 'Sexe',
    'enrollment.childSchoolLevel': 'Classe',
    'enrollment.mainProposition.familyArrangement': "Modalités d'accueil",
    'enrollment.declaredFriends': 'Amis',
    'hostFamily.parent1LastName': 'Famille Hôtesse'
  },
  sortableFields: [
    'enrollment.childLastName',
    'enrollment.childFirstName',
    'enrollment.childBirthDate',
    'enrollment.childGender',
    'enrollment.childSchoolLevel',
    'hostFamily.parent1LastName'
  ],
  customCells: {
    'enrollment.childLastName': ({ enrollment, enrollmentId }) => (
      <TextLink
        href={Paths.ENROLLMENT_PROFILE_PAGE.replace(':id', enrollmentId.toString())}
        name={valueRender(enrollment.childLastName?.toUpperCase())}
      />
    ),
    'enrollment.childFirstName': ({ enrollment }) =>
      valueRender(enrollment.childFirstName, (firstName) => StringFormatter.capitalize(firstName)),
    'enrollment.childBirthDate': ({ enrollment }) =>
      EnrollmentProperties.getAgeAtDeparture(enrollment) ?? '-',
    'enrollment.childGender': ({ enrollment }) =>
      valueRender(enrollment.childGender, (gender) => genderOneLetterMapping[gender].label),
    'enrollment.childSchoolLevel': ({ enrollment }) =>
      valueRender(enrollment.childSchoolLevel, (level) => schoolLevelEnumMapping[level]),
    'enrollment.mainProposition.familyArrangement': ({ enrollment }) => (
      <AccomodationTooltip familyArrangement={enrollment.mainProposition.familyArrangement} />
    ),
    'enrollment.declaredFriends': ({ enrollment }) => (
      <OlCenterEnrollmentTableFriendsTooltip friends={enrollment.declaredFriends} />
    ),
    'hostFamily.parent1LastName': (enrollmentCenter) => {
      const hasHostFamily = !!enrollmentCenter.hostFamily
      return (
        <OlCenterEnrollmentTableHostFamily
          enrollmentCenter={enrollmentCenter}
          buttonProps={{
            label: hasHostFamily ? 'Change host family' : 'Assign host family',
            variant: 'ghost'
          }}
        />
      )
    }
  }
})

const CenterOlListOfStudentsAndStaffs = (props: DataProps<CenterBasic>) => {
  const { id, data } = useDataProps(props, (id) => getCenter(id, 'BASIC'))

  return (
    <div>
      <InnerBox className="bg-white-hover rounded-lg">
        <Typography variant="tag" color={tailwindColors.primary.DEFAULT}>
          Liste des encadrants
        </Typography>
        <div className="h-2" />
        {data && (
          <OlCenterAffectedStaffTable
            accomodation={data.accomodation}
            centerId={id}
            mode="french-view"
            filter={{ isFrench: true }}
          />
        )}
      </InnerBox>
      <div className="h-4" />
      <InnerBox className="bg-white-hover rounded-lg">
        <Typography variant="tag" color={tailwindColors.primary.DEFAULT}>
          Liste des jeunes
        </Typography>
        <div className="h-2" />
        <DynamicDataTable<EnrollmentCenter>
          title="Toutes les inscriptions confirmées"
          className="bg-white-hover p-0"
          bodyVariant="white"
          columns={columns}
          headerType="minimal"
          version="PARTIAL"
          allowSearchParam
          endpoint={constructApiUrl(API.centers.getEnrollments.url, { id })}
          permissions={{ delete: false }}
        />
      </InnerBox>
    </div>
  )
}

export default CenterOlListOfStudentsAndStaffs
